/**
 * timezone.js — Utilitários de data/hora no fuso America/Sao_Paulo (BRT, UTC-3).
 *
 * Todas as funções usam Intl.DateTimeFormat com timeZone: "America/Sao_Paulo",
 * evitando cálculos manuais de offset que falham quando o dispositivo não está em UTC-3.
 *
 * Funções exportadas:
 *   nowBR()                        → timestamp ISO atual em BRT  ("YYYY-MM-DDTHH:MM:SS-03:00")
 *   todayStrBR()                   → data atual em BRT           ("YYYY-MM-DD")
 *   maxDateStrBR(days)             → data atual + N dias em BRT  ("YYYY-MM-DD")
 *   addDaysBR(isoDate, days)       → soma N dias a uma data BRT  ("YYYY-MM-DD")
 *   inactiveDaysUntilBR(days)      → nowBR() + N dias como ISO   ("YYYY-MM-DDTHH:MM:SS-03:00")
 *   parseBRParts(dateOrTimestamp)  → { year, month, day, hour, minute, second, dow }
 *   oneMonthFromNowISO()           → nowBR() + 30 dias como ISO  ("YYYY-MM-DDTHH:MM:SS-03:00")
 */

const TZ = "America/Sao_Paulo";

// ─── INTERNO ──────────────────────────────────────────────────────────────────

/**
 * Extrai as partes numéricas de uma data no fuso BRT usando Intl.
 * Aceita qualquer valor que o construtor Date() aceite.
 */
function _parts(dateInput) {
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  const fmt = new Intl.DateTimeFormat("sv-SE", {
    timeZone: TZ,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    hour12: false,
  });
  const p = {};
  fmt.formatToParts(date).forEach(({ type, value }) => { p[type] = value; });
  return p;
}

// ─── EXPORTADAS ───────────────────────────────────────────────────────────────

/**
 * Timestamp atual no fuso BRT.
 * Retorna "YYYY-MM-DDTHH:MM:SS-03:00".
 * Substitui nowBR() em useAuth.js e todos os arquivos que importavam dali.
 */
export function nowBR() {
  const p = _parts(new Date());
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}:${p.second}-03:00`;
}

/**
 * Data atual em BRT como string "YYYY-MM-DD".
 * Substitui todayBR() em courtAvailability.js.
 */
export function todayStrBR() {
  const p = _parts(new Date());
  return `${p.year}-${p.month}-${p.day}`;
}

/**
 * Data atual + N dias em BRT como string "YYYY-MM-DD".
 * Substitui maxDateBR(days) em courtAvailability.js.
 */
export function maxDateStrBR(days) {
  const future = new Date(Date.now() + days * 86_400_000);
  const p = _parts(future);
  return `${p.year}-${p.month}-${p.day}`;
}

/**
 * Soma N dias a uma string de data ("YYYY-MM-DD") e retorna "YYYY-MM-DD".
 * Usa meio-dia BRT para evitar bugs de horário de verão na aritmética.
 */
export function addDaysBR(isoDate, days) {
  const base = new Date(`${isoDate}T12:00:00-03:00`);
  const result = new Date(base.getTime() + days * 86_400_000);
  const p = _parts(result);
  return `${p.year}-${p.month}-${p.day}`;
}

/**
 * Retorna o timestamp ISO de nowBR() + N dias.
 * Usado em setInactivity() para calcular inactiveUntil.
 * Retorna "YYYY-MM-DDTHH:MM:SS-03:00".
 */
export function inactiveDaysUntilBR(days) {
  const future = new Date(Date.now() + days * 86_400_000);
  const p = _parts(future);
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}:${p.second}-03:00`;
}

/**
 * Retorna nowBR() + 30 dias como ISO string.
 * Substitui o one-liner: new Date(Date.now() + 30 * 86400000).toISOString()
 * usado em registerResult() para blockedAsChallenger/blockedAsChallenged.
 */
export function oneMonthFromNowISO() {
  return inactiveDaysUntilBR(30);
}

/**
 * Extrai partes de um timestamp (ISO string ou Date) interpretadas em BRT.
 * Retorna { year, month, day, hour, minute, second, dow }
 * onde dow é o dia da semana (0 = domingo … 6 = sábado).
 *
 * Substitui os blocos manuais de getEffectiveStartDate() em helpers.js
 * e qualquer outro lugar que precise inspecionar uma data em BRT.
 */
export function parseBRParts(dateInput) {
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  const p = _parts(date);

  // Intl não expõe "weekday" numérico direto, então construímos
  // uma Date meia-noite BRT para perguntar ao JS o dia da semana.
  const midnight = new Date(`${p.year}-${p.month}-${p.day}T12:00:00-03:00`);
  return {
    year:   parseInt(p.year),
    month:  parseInt(p.month),   // 1-based
    day:    parseInt(p.day),
    hour:   parseInt(p.hour),
    minute: parseInt(p.minute),
    second: parseInt(p.second),
    dow:    midnight.getUTCDay(), // 0=Dom … 6=Sáb (UTC day of meia-dia BRT = dia correto)
  };
}

/**
 * Normaliza qualquer timestamp para o formato BRT com sufixo -03:00.
 * Usado em supabase.js (normalizeToBR) para garantir consistência
 * nos dados vindos do banco.
 * Se o string já tem "-03:00", retorna como está.
 */
export function normalizeToBR(dtStr) {
  if (!dtStr) return null;
  if (dtStr.includes("-03:00")) return dtStr;
  const p = _parts(new Date(dtStr));
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}:${p.second}-03:00`;
}
